import React, { useState } from 'react';
import { Box, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconPhotoOff } from '@tabler/icons-react';
import { formatAssetSize, type Asset } from '../../../data/Asset';

type AssetTileProps = {
    asset: Asset;
    selected?: boolean;
    onClick?: () => void;
};

/**
 * A library tile for an uploaded image.
 *
 * Deliberately a sibling of EntityTile rather than a variant of it: EntityTile
 * centres an icon in a flex column, and forcing a full-bleed image into that
 * layout would distort every other tile type. What the two share is the outer
 * frame — border, radius, and hover — so they still read as one grid.
 *
 * The preview sits in a fixed 16:9 box with object-fit: cover, which is what
 * keeps the grid uniform even when an asset has no thumbnail and falls back to
 * the full image.
 */
const AssetTile: React.FC<AssetTileProps> = ({ asset, selected = false, onClick }) => {
    const [failed, setFailed] = useState(false);

    // Thumbnails are best-effort, so falling back to the original is normal —
    // object-fit makes the two indistinguishable apart from bytes over the wire.
    const previewSrc = asset.thumbnailUrl ?? asset.url;
    const usedBy = asset.componentsCount ?? 0;

    return (
        <UnstyledButton onClick={onClick} style={{ display: 'block', width: '100%' }}>
            <Box
                style={{
                    borderRadius: 8,
                    border: selected
                        ? '2px solid var(--mantine-color-violet-6)'
                        : '1px solid var(--mantine-color-default-border)',
                    background: 'var(--mantine-color-body)',
                    overflow: 'hidden',
                    cursor: onClick ? 'pointer' : 'default',
                }}
            >
                <Box
                    style={{
                        aspectRatio: '16 / 9',
                        // Dark ground so a transparent PNG reads as artwork rather
                        // than as a hole in the tile.
                        background: 'var(--mantine-color-dark-6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--mantine-color-dark-2)',
                    }}
                >
                    {failed ? (
                        <IconPhotoOff size={22} stroke={1.5} />
                    ) : (
                        <img
                            src={previewSrc}
                            alt={asset.name}
                            loading="lazy"
                            onError={() => setFailed(true)}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                    )}
                </Box>

                <Stack gap={0} p={8}>
                    <Text size="sm" fw={500} lineClamp={1} title={asset.name}>
                        {asset.name}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>
                        {formatAssetSize(asset.sizeBytes)}
                        {usedBy > 0 && ` · used by ${usedBy} ${usedBy === 1 ? 'component' : 'components'}`}
                    </Text>
                </Stack>
            </Box>
        </UnstyledButton>
    );
};

export default AssetTile;
